(() => {
  'use strict';

  if (window.__corsailleCommerceMeasurementV1) return;
  window.__corsailleCommerceMeasurementV1 = true;

  const VERSION = '1.0.0';
  const ATTRIBUTION_KEY = 'corsaille_measurement_attribution_v1';
  const ATTRIBUTION_TTL_MS = 30 * 60 * 1000;
  const ITEM_SELECTOR = '[data-corsaille-product-card],[data-cplp-feature-stage]';
  const PRODUCT_SELECTOR = '[data-corsaille-product-v2]';
  const LIST_SELECTOR = '[data-corsaille-plp-v4]';
  const observedItems = new WeakSet();
  const measuredItems = new WeakSet();
  const emittedLists = new WeakSet();
  const emittedZeroResults = new WeakSet();
  const emittedProducts = new WeakSet();
  const dwellTimers = new WeakMap();
  let itemObserver = null;
  let sequence = 0;
  let subscriptionsReady = false;
  let consentChecks = 0;
  let consentTimer = 0;

  const q = (selector, context = document) => context.querySelector(selector);
  const qa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const clean = (value, limit = 160) => String(value ?? '').trim().replace(/\s+/g, ' ').slice(0, limit);
  const integer = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const compact = (object) => Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== '' && value !== null && value !== undefined),
  );

  const analyticsAllowed = () => {
    try {
      if (window.Shopify?.designMode === true) return false;
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch {
      return false;
    }
  };

  const pageType = () => {
    if (q(PRODUCT_SELECTOR)) return 'product';
    if (q(LIST_SELECTOR)) return 'collection';
    return 'other';
  };

  const deviceClass = () => {
    if (window.innerWidth < 750) return 'mobile';
    if (window.innerWidth < 990) return 'tablet';
    return 'desktop';
  };

  const experimentVariant = () => clean(
    q(`${LIST_SELECTOR},${PRODUCT_SELECTOR}`)?.dataset.experimentVariant ||
    document.documentElement.dataset.corsailleExperimentVariant || 'control',
    80,
  );

  const emit = (name, detail = {}, ecommerce = null) => {
    if (!analyticsAllowed()) return false;
    const payload = compact({
      event: name,
      corsaille_schema_version: VERSION,
      corsaille_event_sequence: ++sequence,
      corsaille_page_type: pageType(),
      device_class: deviceClass(),
      experiment_variant: experimentVariant(),
      ...detail,
      ecommerce,
    });
    window.dataLayer = window.dataLayer || [];
    if (ecommerce) window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent(`corsaille:measurement:${name}`, { detail: payload }));
    return true;
  };

  const currencyFor = (node) => clean(
    node?.closest?.(`${LIST_SELECTOR},${PRODUCT_SELECTOR}`)?.dataset.currency ||
    q(`${LIST_SELECTOR},${PRODUCT_SELECTOR}`)?.dataset.currency || '',
    8,
  ).toUpperCase();

  const productSource = (node) => node?.closest?.(`${ITEM_SELECTOR},${PRODUCT_SELECTOR}`) || null;

  const itemFrom = (node, overrides = {}) => {
    const source = productSource(node);
    if (!source) return compact(overrides);
    const data = source.dataset;
    const list = source.closest(LIST_SELECTOR);
    const cents = number(overrides.price_cents ?? data.productPriceCents);
    const ribbons = q('[data-cplp-ribbons]', source);
    return compact({
      item_id: clean(overrides.item_id ?? data.productId, 80),
      item_name: clean(overrides.item_name ?? data.productTitle),
      item_brand: 'Corsaille',
      item_variant: clean(overrides.item_variant ?? data.productVariantId, 80),
      item_variant_name: clean(overrides.item_variant_name ?? data.productVariantTitle),
      item_category: clean(data.productFamily, 80),
      item_category2: clean(data.productCategories, 120),
      item_category3: clean(data.productCut, 80),
      item_category4: clean(data.productStoneFamily || data.productColor, 80),
      item_category5: clean(data.productMetalFamily || data.productMetal, 100),
      item_list_id: clean(data.itemListId || list?.dataset.itemListId, 120),
      item_list_name: clean(data.itemListName || list?.dataset.itemListName, 120),
      collection_id: clean(list?.dataset.collectionId, 80),
      index: integer(data.productPosition || data.cplpModulePosition),
      price: cents === null ? null : cents / 100,
      quantity: integer(overrides.quantity),
      corsaille_product_handle: clean(data.productHandle, 120),
      corsaille_available: overrides.available ?? (data.productAvailable === '' ? null : data.productAvailable === 'true'),
      corsaille_configuration_mode: clean(data.productConfigurationMode, 60),
      corsaille_price_scope: clean(data.productPriceScope, 60),
      corsaille_stone_origin: clean(data.productStoneOrigin || data.productOrigin, 100),
      corsaille_ribbon: clean(ribbons?.getAttribute('aria-label'), 100),
      corsaille_ribbon_source: clean(ribbons?.dataset.cplpRibbonSource, 60),
      corsaille_truth_fallback: data.cplpFallback === '' ? null : data.cplpFallback === 'true',
      ...overrides.item_parameters,
    });
  };

  const ecommerceFor = (items, currency = '', value = null) => compact({
    currency: clean(currency, 8).toUpperCase(),
    value: number(value),
    items: items.filter((item) => item?.item_id || item?.item_name),
  });

  const safeDestination = (href) => {
    if (!href) return '';
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin ? url.pathname : url.hostname;
    } catch {
      return '';
    }
  };

  const filterSnapshot = (entries) => {
    const values = new Map();
    for (const [rawName, rawValue] of entries) {
      const name = clean(rawName, 120);
      if (!name.startsWith('filter.')) continue;
      const value = clean(rawValue, 120);
      if (!value) continue;
      if (!values.has(name)) values.set(name, []);
      if (!values.get(name).includes(value)) values.get(name).push(value);
    }
    return Array.from(values, ([name, selected_values]) => ({ name, selected_values }));
  };

  const currentFilters = () => filterSnapshot(new URLSearchParams(location.search).entries());

  const selectedOptions = (root) => qa('variant-selects [data-option-name]', root).map((field) => {
    const input = q('input[type="radio"]:checked,select', field);
    return compact({
      name: clean(field.dataset.optionName || input?.dataset.optionName, 80),
      value: clean(input?.value, 100),
    });
  }).filter((entry) => entry.name || entry.value).slice(0, 6);

  const listContext = (root) => {
    const params = new URLSearchParams(location.search);
    const filters = currentFilters();
    return compact({
      collection_id: clean(root.dataset.collectionId, 80),
      collection_handle: clean(root.dataset.collectionHandle, 120),
      collection_family: clean(root.dataset.catalogueFamily, 80),
      collection_family_source: clean(root.dataset.catalogueFamilySource, 80),
      item_list_id: clean(root.dataset.itemListId, 120),
      item_list_name: clean(root.dataset.itemListName, 120),
      page_number: integer(root.dataset.pageNumber || params.get('page') || 1),
      page_size: integer(root.dataset.productsPerPage),
      result_count: integer(root.dataset.resultCount || q('[data-cplp-result-count]', root)?.textContent),
      rendered_product_count: qa('[data-corsaille-product-card]', root).length,
      active_filter_count: integer(root.dataset.activeFilterCount) ?? filters.reduce((total, filter) => total + filter.selected_values.length, 0),
      filters,
      sort_by: clean(root.dataset.sortBy || params.get('sort_by'), 100),
    });
  };

  const writeAttribution = (item) => {
    if (!analyticsAllowed() || !item?.item_id) return;
    try {
      sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({
        product_id: item.item_id,
        item_list_id: item.item_list_id || '',
        item_list_name: item.item_list_name || '',
        collection_id: clean(item.collection_id, 80),
        index: item.index ?? null,
        stored_at: Date.now(),
      }));
    } catch {
      // Attribution is optional; navigation must never depend on storage.
    }
  };

  const consumeAttribution = (productId) => {
    if (!analyticsAllowed()) return null;
    try {
      const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
      if (!raw) return null;
      const value = JSON.parse(raw);
      const valid = value && String(value.product_id) === String(productId) &&
        Date.now() - Number(value.stored_at) <= ATTRIBUTION_TTL_MS;
      if (!valid) {
        sessionStorage.removeItem(ATTRIBUTION_KEY);
        return null;
      }
      sessionStorage.removeItem(ATTRIBUTION_KEY);
      return compact({
        origin_item_list_id: clean(value.item_list_id, 120),
        origin_item_list_name: clean(value.item_list_name, 120),
        origin_collection_id: clean(value.collection_id, 80),
        origin_item_index: integer(value.index),
      });
    } catch {
      return null;
    }
  };

  const emitListView = (root) => {
    if (emittedLists.has(root) || !analyticsAllowed()) return;
    const items = qa('[data-corsaille-product-card]', root).map((card) => itemFrom(card));
    const context = listContext(root);
    if (emit('plp_view', context, ecommerceFor(items, currencyFor(root)))) {
      emittedLists.add(root);
      root.__corsailleMeasurementViewStartedAt = performance.now();
    }
    if (context.result_count === 0 && !emittedZeroResults.has(root) && emit('plp_zero_results', context)) {
      emittedZeroResults.add(root);
    }
  };

  const emitProductView = (root) => {
    if (emittedProducts.has(root) || !analyticsAllowed()) return;
    const item = itemFrom(root);
    const attribution = consumeAttribution(item.item_id) || {};
    const currency = currencyFor(root);
    const detail = compact({
      product_id: item.item_id,
      product_handle: item.corsaille_product_handle,
      product_family: item.item_category,
      variant_id: item.item_variant,
      available: item.corsaille_available,
      ...attribution,
    });
    if (emit('pdp_view', detail, ecommerceFor([item], currency, item.price))) emittedProducts.add(root);
  };

  const impressionQualifies = (entry) => {
    if (!entry.isIntersecting) return false;
    const requiredHeight = Math.min(180, Math.max(80, entry.boundingClientRect.height * 0.2));
    return entry.intersectionRect.height >= requiredHeight && entry.intersectionRect.width >= 80;
  };

  const setupItemObserver = () => {
    if (!analyticsAllowed() || !('IntersectionObserver' in window)) return;
    if (!itemObserver) {
      itemObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const node = entry.target;
          if (measuredItems.has(node)) continue;
          if (!impressionQualifies(entry)) {
            const timer = dwellTimers.get(node);
            if (timer) clearTimeout(timer);
            dwellTimers.delete(node);
            continue;
          }
          if (dwellTimers.has(node)) continue;
          const timer = setTimeout(() => {
            dwellTimers.delete(node);
            if (!analyticsAllowed() || measuredItems.has(node)) return;
            const rect = node.getBoundingClientRect();
            const visibleHeight = Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0);
            const visibleWidth = Math.min(rect.right, innerWidth) - Math.max(rect.left, 0);
            if (visibleHeight < Math.min(180, Math.max(80, rect.height * 0.2)) || visibleWidth < 80) return;
            const item = itemFrom(node);
            if (emit('plp_product_impression', {
              item_list_id: item.item_list_id,
              item_list_name: item.item_list_name,
              item_index: item.index,
              impression_context: node.matches('[data-cplp-feature-stage]') ? 'feature_stage' : 'product_grid',
            }, ecommerceFor([item], currencyFor(node)))) {
              measuredItems.add(node);
              itemObserver?.unobserve(node);
            }
          }, 400);
          dwellTimers.set(node, timer);
        }
      }, { threshold: [0, 0.1, 0.2, 0.35] });
    }
    for (const node of qa(ITEM_SELECTOR)) {
      if (observedItems.has(node) || measuredItems.has(node)) continue;
      observedItems.add(node);
      itemObserver.observe(node);
    }
  };

  const paymentDetail = (node) => {
    const disclosure = node?.closest?.('[data-corsaille-payment-disclosure]') || node;
    const product = q(PRODUCT_SELECTOR);
    return compact({
      product_id: clean(product?.dataset.productId, 80),
      variant_id: clean(product?.dataset.productVariantId, 80),
      payment_mode: clean(disclosure?.dataset.paymentMode, 80),
      payment_provider: clean(disclosure?.dataset.paymentProvider, 100),
    });
  };

  const initRoots = (scope = document) => {
    const listRoots = [];
    const productRoots = [];
    if (scope instanceof Element && scope.matches(LIST_SELECTOR)) listRoots.push(scope);
    if (scope instanceof Element && scope.matches(PRODUCT_SELECTOR)) productRoots.push(scope);
    listRoots.push(...qa(LIST_SELECTOR, scope));
    productRoots.push(...qa(PRODUCT_SELECTOR, scope));
    for (const root of listRoots) {
      qa('[data-cplp-sort],[data-cplp-facet-sort]', root).forEach((select) => {
        if (!select.dataset.corsaillePreviousValue) select.dataset.corsaillePreviousValue = select.value;
      });
      emitListView(root);
    }
    for (const root of productRoots) emitProductView(root);
    setupItemObserver();
  };

  const itemsWithin = (scope) => {
    const items = [];
    if (scope instanceof Element && scope.matches(ITEM_SELECTOR)) items.push(scope);
    if (scope?.querySelectorAll) items.push(...qa(ITEM_SELECTOR, scope));
    return items;
  };

  const pauseItemMeasurement = (scope = document) => {
    for (const item of itemsWithin(scope)) {
      const timer = dwellTimers.get(item);
      if (timer) clearTimeout(timer);
      dwellTimers.delete(item);
      itemObserver?.unobserve(item);
      observedItems.delete(item);
    }
  };

  const cleanupScope = (scope) => {
    pauseItemMeasurement(scope);
    const listRoots = [];
    const productRoots = [];
    if (scope instanceof Element && scope.matches(LIST_SELECTOR)) listRoots.push(scope);
    if (scope instanceof Element && scope.matches(PRODUCT_SELECTOR)) productRoots.push(scope);
    if (scope?.querySelectorAll) {
      listRoots.push(...qa(LIST_SELECTOR, scope));
      productRoots.push(...qa(PRODUCT_SELECTOR, scope));
    }
    for (const root of listRoots) {
      emittedLists.delete(root);
      emittedZeroResults.delete(root);
      delete root.__corsailleMeasurementViewStartedAt;
    }
    for (const root of productRoots) {
      emittedProducts.delete(root);
      delete root.__corsailleMeasurementAttempt;
      delete root.__corsailleMeasurementVariantId;
    }
  };

  const clickPlacement = (link) => {
    if (link.matches('.cplp-card__media-link,.cplp-feature__media')) return 'media';
    if (link.matches('.cplp-card__title')) return 'title';
    if (link.matches('.cplp-card__action,.cplp-feature__link')) return 'primary_action';
    return 'link';
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const save = target.closest('[data-corsaille-save]');
    if (save) {
      const before = save.getAttribute('aria-pressed');
      setTimeout(() => {
        const after = save.getAttribute('aria-pressed');
        if (after === before || (after !== 'true' && after !== 'false')) return;
        const item = itemFrom(save);
        if (save.closest(LIST_SELECTOR)) {
          emit('plp_save', {
            save_action: after === 'true' ? 'saved' : 'removed',
            item_list_id: item.item_list_id,
            item_index: item.index,
          }, ecommerceFor([item], currencyFor(save)));
        }
      }, 0);
      return;
    }

    const productLink = target.closest('[data-cplp-product-select]');
    if (productLink && productLink.dataset.corsailleEvent !== 'plp_consultation_click') {
      const item = itemFrom(productLink);
      const listRoot = productLink.closest(LIST_SELECTOR);
      if (emit('plp_product_select', {
        selection_placement: clickPlacement(productLink),
        destination: safeDestination(productLink.href),
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
        item_index: item.index,
        milliseconds_since_plp_view: listRoot?.__corsailleMeasurementViewStartedAt == null ? null :
          Math.max(0, Math.round(performance.now() - listRoot.__corsailleMeasurementViewStartedAt)),
      }, ecommerceFor([item], currencyFor(productLink)))) writeAttribution(item);
      return;
    }

    const consultation = target.closest('[data-corsaille-consultation],[data-corsaille-event="plp_consultation_click"]');
    if (consultation) {
      const item = itemFrom(consultation);
      const onProductPage = Boolean(consultation.closest(PRODUCT_SELECTOR));
      emit(onProductPage ? 'pdp_consultation_start' : 'plp_consultation_click', {
        consultation_context: onProductPage ? 'product_page' : 'product_list',
        consultation_placement: consultation.closest('.cplp-hero') ? 'hero' :
          consultation.closest('[data-cplp-editorial]') ? 'editorial' :
          consultation.closest('[data-corsaille-product-card]') ? 'product_card' : 'purchase_section',
        destination: safeDestination(consultation.href),
        product_id: item.item_id,
        item_list_id: item.item_list_id,
        item_index: item.index,
      }, item.item_id ? ecommerceFor([item], currencyFor(consultation)) : null);
      return;
    }

    const paymentTerms = target.closest('[data-corsaille-payment-terms],[data-corsaille-event="pdp_payment_terms_open"]');
    if (paymentTerms) {
      emit('pdp_payment_terms_open', {
        ...paymentDetail(paymentTerms),
        destination: safeDestination(paymentTerms.href),
      });
      return;
    }

    const paymentProviderStart = target.closest('[data-corsaille-payment-provider-start]');
    if (paymentProviderStart) {
      const disclosure = paymentProviderStart.closest('[data-corsaille-payment-disclosure]') || q('[data-corsaille-payment-disclosure]');
      const detail = paymentDetail(disclosure || paymentProviderStart);
      if (detail.payment_mode && detail.payment_provider) {
        emit('pdp_payment_provider_start', {
          ...detail,
          provider_action: clean(paymentProviderStart.dataset.paymentProviderAction || 'start', 60),
        });
      }
      return;
    }

    const filterOpen = target.closest('[data-cplp-facets-open]');
    if (filterOpen) {
      const root = filterOpen.closest(LIST_SELECTOR);
      emit('plp_filter_open', listContext(root));
      return;
    }

    const filterClear = target.closest('[data-cplp-filter-clear],[data-cplp-filter-remove]');
    if (filterClear) {
      const root = filterClear.closest(LIST_SELECTOR);
      emit('plp_filter_clear', {
        ...listContext(root),
        clear_scope: filterClear.matches('[data-cplp-filter-clear]') ? 'all' : 'single',
        filter_label: clean(filterClear.textContent, 120),
        destination: safeDestination(filterClear.href),
      });
      return;
    }

    const configure = target.closest('[data-corsaille-event="pdp_configure_jump"]');
    if (configure) {
      const root = configure.closest(PRODUCT_SELECTOR);
      const item = itemFrom(root);
      root.__corsailleMeasurementConfigurationStarted = true;
      return;
    }

    const dock = target.closest('[data-cv2-dock-submit]');
    if (dock) {
      const root = q(PRODUCT_SELECTOR);
      const item = itemFrom(root);
      root.__corsailleMeasurementPurchaseSource = 'sticky_dock';
      return;
    }

    const gallery = target.closest('[data-cv2-thumb],[data-cv2-gallery-prev],[data-cv2-gallery-next],[data-cv2-gallery-open],[data-cv2-modal-prev],[data-cv2-modal-next]');
    if (gallery) {
      const root = gallery.closest(PRODUCT_SELECTOR) || q(PRODUCT_SELECTOR);
      root.__corsailleMeasurementGalleryEngaged = true;
      return;
    }

    const sizeMode = target.closest('[data-cv2-size-mode]');
    if (sizeMode) {
      const root = sizeMode.closest(PRODUCT_SELECTOR);
      root.__corsailleMeasurementSizeMode = clean(sizeMode.dataset.cv2SizeMode, 40);
      return;
    }

    const sizeGuide = target.closest('[data-cv2-size-guide-open],[data-cv2-size-guide-use]');
    if (sizeGuide) {
      const root = sizeGuide.closest(PRODUCT_SELECTOR);
      emit('pdp_size_guide', {
        product_id: clean(root?.dataset.productId, 80),
        variant_id: clean(root?.dataset.productVariantId, 80),
        size_guide_action: sizeGuide.hasAttribute('data-cv2-size-guide-use') ? 'use_recommendation' : 'open',
        pair_sizing: root?.dataset.pairSizing === 'true',
      });
      return;
    }

    const certificate = target.closest('[data-corsaille-event="certificate_open"]');
    if (certificate) {
      const root = certificate.closest(PRODUCT_SELECTOR);
      root.__corsailleMeasurementCertificateOpened = true;
      return;
    }

    const share = target.closest('[data-corsaille-event="product_share"]');
    if (share) {
      const root = share.closest(PRODUCT_SELECTOR);
      root.__corsailleMeasurementShareStarted = true;
      return;
    }

    const plpCta = target.closest('[data-corsaille-event="plp_hero_cta"],[data-corsaille-event="plp_style_select"],[data-corsaille-event="plp_editorial_click"]');
    if (plpCta) {
      const root = plpCta.closest(LIST_SELECTOR);
      emit(clean(plpCta.dataset.corsailleEvent, 80), {
        cta_type: clean(plpCta.dataset.corsailleEvent, 80),
        cta_label: clean(plpCta.textContent, 120),
        destination: safeDestination(plpCta.href),
        ...listContext(root),
      });
    }
  }, { capture: true });

  document.addEventListener('change', (event) => {
    const input = event.target instanceof Element ? event.target : null;
    if (!input) return;
    const root = input.closest(LIST_SELECTOR);
    if (input.matches('[data-cplp-sort],[data-cplp-facet-sort]') && root) {
      const previous = clean(input.dataset.corsaillePreviousValue, 100);
      const selected = input instanceof HTMLSelectElement ? input.selectedOptions[0] : null;
      emit('plp_sort', {
        collection_id: clean(root.dataset.collectionId, 80),
        sort_from: previous,
        sort_to: clean(input.value, 100),
        sort_label: clean(selected?.textContent, 100),
        sort_context: input.matches('[data-cplp-facet-sort]') ? 'facet_drawer' : 'toolbar',
      });
      input.dataset.corsaillePreviousValue = input.value;
      return;
    }
    if (input.matches('[data-cplp-filter-input]') && root) return;
    if (input.matches('[data-cv2-known-size],[data-cv2-measure-value]')) {
      const product = input.closest(PRODUCT_SELECTOR);
      if (product) product.__corsailleMeasurementSizeInputProvided = Boolean(clean(input.value, 20));
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;
    const listRoot = form.closest(LIST_SELECTOR);
    if (form.matches('[data-cplp-facets-form]') && listRoot) {
      const formData = new FormData(form);
      const filters = filterSnapshot(formData.entries());
      emit('plp_filter_apply', {
        collection_id: clean(listRoot.dataset.collectionId, 80),
        collection_handle: clean(listRoot.dataset.collectionHandle, 120),
        filters,
        active_filter_count: filters.reduce((total, filter) => total + filter.selected_values.length, 0),
        sort_by: clean(formData.get('sort_by'), 100),
      });
      return;
    }
    if (!form.matches('form[data-type="add-to-cart-form"]')) return;
    const root = form.closest(PRODUCT_SELECTOR);
    if (!root) return;
    const formData = new FormData(form);
    const variantId = clean(formData.get('id'), 80);
    const quantity = integer(formData.get('quantity')) || 1;
    const item = itemFrom(root, { item_variant: variantId, quantity });
    const sizeProperties = [];
    formData.forEach((value, key) => {
      if (/^properties\[.*m[ăa]r/i.test(key)) sizeProperties.push(Boolean(clean(value, 20)));
    });
    root.__corsailleMeasurementAttempt = { item, quantity, variantId };
    const purchaseSource = root.__corsailleMeasurementPurchaseSource || 'purchase_section';
    delete root.__corsailleMeasurementPurchaseSource;
    root.__corsailleMeasurementAttempt = {
      ...root.__corsailleMeasurementAttempt,
      selectedOptions: selectedOptions(root),
      sizeFieldsPresent: sizeProperties.length,
      sizeFieldsCompleted: sizeProperties.filter(Boolean).length,
      purchaseSource,
    };
  });

  const setupSubscriptions = () => {
    const subscribeFunction = typeof subscribe === 'function' ? subscribe : window.subscribe;
    const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
    if (subscriptionsReady || typeof subscribeFunction !== 'function' || !events) return;
    subscriptionsReady = true;
    if (events.variantChange) {
      subscribeFunction(events.variantChange, ({ data } = {}) => {
        const sectionId = clean(data?.sectionId, 100);
        const root = sectionId ? q(`${PRODUCT_SELECTOR}[data-section="${CSS.escape(sectionId)}"]`) : q(PRODUCT_SELECTOR);
        const variant = data?.variant;
        if (!root || !variant?.id) return;
        const variantId = String(variant.id);
        if (root.__corsailleMeasurementVariantId === variantId) return;
        root.__corsailleMeasurementVariantId = variantId;
        root.dataset.productVariantId = variantId;
        root.dataset.productVariantTitle = clean(variant.title, 120);
        root.dataset.productAvailable = variant.available === false ? 'false' : 'true';
        if (variant.price != null) root.dataset.productPriceCents = String(variant.price);
        const item = itemFrom(root, {
          item_variant: variantId,
          item_variant_name: variant.title,
          price_cents: variant.price,
          available: variant.available !== false,
        });
        emit('pdp_variant_change', {
          product_id: item.item_id,
          variant_id: variantId,
          variant_available: variant.available !== false,
          selected_options: selectedOptions(root),
        }, ecommerceFor([item], currencyFor(root), item.price));
      });
    }
    if (events.cartUpdate) {
      subscribeFunction(events.cartUpdate, (event = {}) => {
        if (event.source !== 'product-form') return;
        const root = q(PRODUCT_SELECTOR);
        const attempt = root?.__corsailleMeasurementAttempt || {};
        const cartData = event.cartData || {};
        const variantId = clean(event.productVariantId || cartData.variant_id || cartData.id || attempt.variantId, 80);
        const quantity = integer(cartData.quantity || attempt.quantity) || 1;
        const priceCents = number(cartData.final_price ?? cartData.price ?? root?.dataset.productPriceCents);
        const item = itemFrom(root, {
          item_id: cartData.product_id || attempt.item?.item_id,
          item_name: cartData.product_title || attempt.item?.item_name,
          item_variant: variantId,
          item_variant_name: cartData.variant_title || attempt.item?.item_variant_name,
          price_cents: priceCents,
          quantity,
        });
        emit('pdp_add_to_cart', {
          product_id: item.item_id,
          variant_id: variantId,
          quantity,
          selected_options: attempt.selectedOptions || [],
          size_fields_present: integer(attempt.sizeFieldsPresent) || 0,
          size_fields_completed: integer(attempt.sizeFieldsCompleted) || 0,
          purchase_source: clean(attempt.purchaseSource || 'purchase_section', 40),
        }, ecommerceFor([item], currencyFor(root), item.price === null ? null : item.price * quantity));
        if (root) delete root.__corsailleMeasurementAttempt;
      });
    }
    if (events.cartError) {
      subscribeFunction(events.cartError, (event = {}) => {
        if (event.source !== 'product-form') return;
        const root = q(PRODUCT_SELECTOR);
        emit('product_error', {
          product_id: clean(root?.dataset.productId, 80),
          variant_id: clean(event.productVariantId, 80),
          error_code: 'CART_REJECTED',
          error_stage: 'add_to_cart',
        });
        if (root) delete root.__corsailleMeasurementAttempt;
      });
    }
  };

  const handleConsentState = () => {
    if (!analyticsAllowed()) {
      try { sessionStorage.removeItem(ATTRIBUTION_KEY); } catch { /* no-op */ }
      pauseItemMeasurement();
      return;
    }
    if (consentTimer) {
      clearInterval(consentTimer);
      consentTimer = 0;
    }
    initRoots();
  };

  const init = () => {
    setupSubscriptions();
    initRoots();
    handleConsentState();
    if (!analyticsAllowed() && !consentTimer) {
      consentTimer = window.setInterval(() => {
        consentChecks += 1;
        setupSubscriptions();
        if (analyticsAllowed()) handleConsentState();
        if (consentChecks >= 40 && consentTimer) {
          clearInterval(consentTimer);
          consentTimer = 0;
        }
      }, 250);
    }
  };

  document.addEventListener('visitorConsentCollected', handleConsentState);
  document.addEventListener('shopify:section:load', (event) => initRoots(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupScope(event.target));
  window.addEventListener('pageshow', handleConsentState);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.CorsailleMeasurementV1 = Object.freeze({
    version: VERSION,
    track: emit,
    analyticsAllowed,
  });
})();
